import { z } from "zod";

/**
 * Advanced UI/UX settings - SHARED across all deployment types.
 *
 * These settings control the user experience:
 * - Tool visibility (input/output display)
 * - Document capabilities
 * - UI features (download, copy, refresh)
 *
 * Used by: Email, Telegram, Embed, PhoneCall, etc.
 */
export const AdvancedSettingsSchema = z.object({
  /**
   * Show tool input parameters to users.
   * When true, users see what data is sent to tools.
   */
  showToolsInput: z.boolean().default(false),

  /**
   * Show tool output/results to users.
   * When true, users see tool responses.
   */
  showToolsOutput: z.boolean().default(false),

  /**
   * Allow documents to be shared in conversations.
   * Enables file sharing and document previews in chat.
   */
  enableDocumentsInChat: z.boolean().default(true),

  /**
   * Show download and copy buttons on messages.
   * Allows users to save or copy AI responses.
   */
  showDownloadAndCopyButtons: z.boolean().default(true),

  /**
   * Allow users to refresh/restart conversations.
   * Shows a "New conversation" or refresh button.
   */
  enabledRefreshConversation: z.boolean().default(true),

  /**
   * Embed only (NEX-2541): advertise the `send_message` card tool so the agent
   * can send structured cards (content, footer, media, quick-reply buttons).
   */
  enableAgentCardMessages: z.boolean().default(true),

  /**
   * Embed only (NEX-2541): advertise the `display_html` tool so the agent can
   * render free-form sanitized HTML directly in the chat widget.
   */
  enableAgentDisplayHtml: z.boolean().default(true),

  /**
   * Embed only (NEX-4252): a `send_message` card that is the LAST tool call of
   * its assistant message ends the agent's turn — the embed twin of the
   * WhatsApp deployment's `cardEndsTurn` (NEX-4407). Default ON: it removes the
   * forced follow-up completion the model fills with filler or narration after
   * the card ("(waiting for user's reply)"). A deployment whose prompt sends a
   * holding card ALONE and continues in a later completion opts OUT with
   * `false`. A card followed by more calls in the same assistant message is
   * unaffected either way.
   */
  cardEndsTurn: z.boolean().default(true),

  // ---------------------------------------------------------
  // DEPRECATED FIELDS - Kept for backwards compatibility
  // These features have been removed or are always on
  // ---------------------------------------------------------

  /** @deprecated Commands feature removed */
  enableCommands: z.boolean().optional(),

  /** @deprecated Describe mode deprecated */
  enableDescribeMode: z.boolean().optional()
});

export type AdvancedSettings = z.infer<typeof AdvancedSettingsSchema>;

/**
 * Default advanced settings for new deployments.
 * Provides a sensible default configuration.
 */
export const DEFAULT_ADVANCED_SETTINGS: AdvancedSettings = {
  showToolsInput: false,
  showToolsOutput: false,
  enableDocumentsInChat: true,
  showDownloadAndCopyButtons: true,
  enabledRefreshConversation: true,
  enableAgentCardMessages: true,
  enableAgentDisplayHtml: true,
  cardEndsTurn: true
};
